import React, { useState } from 'react';
import { Save, RefreshCw, Info, Trash2 } from 'lucide-react';

const AdminMatriceTauxOptimises = () => {
  const [hasChanges, setHasChanges] = useState(false);
  const [seuils, setSeuils] = useState([
    { id: 1, label: '0,5% - 2,25%', min: 0.005, max: 0.0225 },
    { id: 2, label: '2,25% - 6,75%', min: 0.0225, max: 0.0675 },
    { id: 3, label: '6,75% - 13,5%', min: 0.0675, max: 0.135 },
    { id: 4, label: '13,5% - 100%', min: 0.135, max: 1 },
    { id: 5, label: '> 100%', min: 1, max: Infinity }
  ]);
  
  const [motifs, setMotifs] = useState([
    { id: 'electro_intensive', label: 'Activité Électro-intensive', color: 'bg-[#397DFF]' },
    { id: 'fuite_carbone', label: 'Fuite de Carbone', color: 'bg-[#F9C80E]' },
    { id: 'exoneration', label: 'Exonération', color: 'bg-[#10D183]' }
  ]);
  
  const [matrice, setMatrice] = useState({
    1: { electro_intensive: 7.5, fuite_carbone: 5.5, exoneration: 0 },
    2: { electro_intensive: 5, fuite_carbone: 2.5, exoneration: 0 },
    3: { electro_intensive: 2, fuite_carbone: 1, exoneration: 0 },
    4: { electro_intensive: 2, fuite_carbone: 0.5, exoneration: 0 },
    5: { electro_intensive: 2, fuite_carbone: 0.5, exoneration: 0 }
  });

  const [showAddSeuil, setShowAddSeuil] = useState(false);
  const [showAddMotif, setShowAddMotif] = useState(false);
  const [newSeuil, setNewSeuil] = useState({ label: '', min: '', max: '' });
  const [newMotif, setNewMotif] = useState({ label: '', color: 'bg-[#397DFF]' });

  const handleSave = () => {
    if (!window.confirm('Enregistrer la matrice ?')) return;
    // TODO: API
    alert('Matrice sauvegardée !');
    setHasChanges(false);
  };

  const handleReset = () => {
    if (!window.confirm('Annuler modifications ?')) return;
    window.location.reload();
  };

  const handleAddSeuil = () => {
    const newId = Math.max(...seuils.map(s => s.id)) + 1;
    const newSeuilObj = {
      id: newId,
      label: newSeuil.label,
      min: parseFloat(newSeuil.min),
      max: parseFloat(newSeuil.max) || Infinity
    };
    
    setSeuils([...seuils, newSeuilObj]);
    
    // Initialiser taux pour ce seuil
    const newMatrice = { ...matrice };
    newMatrice[newId] = {};
    motifs.forEach(m => {
      newMatrice[newId][m.id] = 0;
    });
    setMatrice(newMatrice);
    
    setShowAddSeuil(false);
    setNewSeuil({ label: '', min: '', max: '' });
    setHasChanges(true);
    alert('Nouveau seuil ajouté');
  };

  const handleAddMotif = () => {
    const newId = newMotif.label.toLowerCase().replace(/\s+/g, '_');
    
    if (motifs.find(m => m.id === newId)) {
      alert('Ce motif existe déjà');
      return;
    }
    
    const newMotifObj = {
      id: newId,
      label: newMotif.label,
      color: newMotif.color
    };
    
    setMotifs([...motifs, newMotifObj]);
    
    // Initialiser taux pour ce motif sur tous les seuils
    const newMatrice = { ...matrice };
    seuils.forEach(s => {
      if (!newMatrice[s.id]) newMatrice[s.id] = {};
      newMatrice[s.id][newId] = 0;
    });
    setMatrice(newMatrice);
    
    setShowAddMotif(false);
    setNewMotif({ label: '', color: 'bg-[#397DFF]' });
    setHasChanges(true);
    alert('Nouveau motif ajouté');
  };

  const updateSeuil = (seuilId, field, value) => {
    const newSeuils = seuils.map(s => 
      s.id === seuilId ? { ...s, [field]: field === 'label' ? value : parseFloat(value) || 0 } : s
    );
    setSeuils(newSeuils);
    setHasChanges(true);
  };

  const deleteSeuil = (seuilId) => {
    if (seuils.length <= 1) {
      alert('Impossible de supprimer le dernier seuil');
      return;
    }
    if (!window.confirm('Supprimer ce seuil de ratio ?')) return;
    
    setSeuils(seuils.filter(s => s.id !== seuilId));
    
    // Supprimer de la matrice
    const newMatrice = { ...matrice };
    delete newMatrice[seuilId];
    setMatrice(newMatrice);
    
    setHasChanges(true);
    alert('Seuil supprimé');
  };

  const deleteMotif = (motifId) => {
    if (motifs.length <= 1) {
      alert('Impossible de supprimer le dernier motif');
      return;
    }
    if (!window.confirm('Supprimer ce motif d\'éligibilité ?')) return;
    
    setMotifs(motifs.filter(m => m.id !== motifId));
    
    // Supprimer de la matrice
    const newMatrice = { ...matrice };
    Object.keys(newMatrice).forEach(seuilId => {
      delete newMatrice[seuilId][motifId];
    });
    setMatrice(newMatrice);
    
    setHasChanges(true);
    alert('Motif supprimé');
  };

  const updateTaux = (ligne, motif, valeur) => {
    const newMatrice = { ...matrice };
    if (!newMatrice[ligne]) newMatrice[ligne] = {};
    newMatrice[ligne][motif] = parseFloat(valeur) || 0;
    setMatrice(newMatrice);
    setHasChanges(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-semibold text-[#191D1E] tracking-tight">
            Matrice Taux Optimisés
          </h1>
          <p className="text-[#687175] mt-2">
            Gestion des taux TICFE optimisés selon ratio TICFE/VA et motif d'éligibilité
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowAddSeuil(true)} className="flex items-center gap-2 border border-[#397DFF] text-[#397DFF] hover:bg-[#397DFF] hover:text-white font-medium py-3 px-4 rounded-lg">
            + Seuil
          </button>
          <button onClick={() => setShowAddMotif(true)} className="flex items-center gap-2 border border-[#F9C80E] text-[#F9C80E] hover:bg-[#F9C80E] hover:text-white font-medium py-3 px-4 rounded-lg">
            + Motif
          </button>
          {hasChanges && (
            <button onClick={handleReset} className="flex items-center gap-2 border border-[#BFC6CC] text-[#687175] hover:bg-[#F5F6F9] font-medium py-3 px-6 rounded-lg">
              <RefreshCw className="w-5 h-5" /> Annuler
            </button>
          )}
          <button onClick={handleSave} disabled={!hasChanges} className="flex items-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white font-medium py-3 px-6 rounded-lg disabled:opacity-50">
            <Save className="w-5 h-5" /> Enregistrer
          </button>
        </div>
      </div>

      {/* Explication */}
      <div className="mb-6 p-6 bg-[#E8F4FD] border border-[#397DFF] rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-[#397DFF] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[#191D1E]">
            <p className="font-semibold mb-2">Comment fonctionne la matrice ?</p>
            <p>Le taux TICFE optimisé est déterminé par :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-[#687175]">
              <li><strong>Ligne</strong> : Ratio TICFE/Valeur Ajoutée du client</li>
              <li><strong>Colonne</strong> : Motif d'éligibilité du code NAF</li>
              <li><strong>Valeur</strong> : Taux optimisé en €/MWh (intersection ligne × colonne)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Message modifications */}
      {hasChanges && (
        <div className="mb-6 p-4 bg-[#F9C80E]/10 border border-[#F9C80E] rounded-lg">
          <p className="text-sm text-[#191D1E]">⚠️ Vous avez des modifications non enregistrées</p>
        </div>
      )}

      {/* Tableau Matrice */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F5F6F9]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E] border-r border-[#BFC6CC]">
                  Ratio TICFE/VA
                </th>
                {motifs.map((motif) => (
                  <th key={motif.id} className="px-6 py-4 text-center text-sm font-semibold text-[#191D1E] border-l border-[#BFC6CC] hover:bg-[#F5F6F9] transition-colors group/col">
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${motif.color}`}></div>
                        <button
                          onClick={() => deleteMotif(motif.id)}
                          className="text-red-600 hover:text-red-700 opacity-0 group-hover/col:opacity-100 transition-opacity"
                          title="Supprimer ce motif"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div>{motif.label}</div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#BFC6CC]">
              {seuils.map((seuil) => (
                <tr key={seuil.id} className="hover:bg-[#F5F6F9]/50 group">
                  <td className="px-6 py-4 border-r border-[#BFC6CC]">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={seuil.label}
                          onChange={(e) => updateSeuil(seuil.id, 'label', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-[#BFC6CC] rounded focus:ring-2 focus:ring-[#397DFF]"
                          placeholder="Ex: 0,5% - 2,25%"
                        />
                        <div className="flex gap-2 text-xs">
                          <input
                            type="number"
                            step="0.001"
                            value={seuil.min * 100}
                            onChange={(e) => updateSeuil(seuil.id, 'min', parseFloat(e.target.value) / 100)}
                            className="w-20 px-2 py-1 border border-[#BFC6CC] rounded"
                            placeholder="Min %"
                          />
                          <span className="self-center text-[#687175]">-</span>
                          <input
                            type="number"
                            step="0.001"
                            value={seuil.max === Infinity ? '' : seuil.max * 100}
                            onChange={(e) => updateSeuil(seuil.id, 'max', e.target.value ? parseFloat(e.target.value) / 100 : Infinity)}
                            className="w-20 px-2 py-1 border border-[#BFC6CC] rounded"
                            placeholder="Max %"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => deleteSeuil(seuil.id)}
                        className="text-red-600 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Supprimer ce seuil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  {motifs.map((motif) => (
                    <td key={`${seuil.id}-${motif.id}`} className="px-4 py-3 border-l border-[#BFC6CC]">
                      <div className="flex items-center justify-center gap-2">
                        <input
                          type="number"
                          step="0.01"
                          value={matrice[seuil.id][motif.id] || ''}
                          onChange={(e) => updateTaux(seuil.id, motif.id, e.target.value)}
                          className="w-24 px-3 py-2 text-center border border-[#BFC6CC] rounded focus:ring-2 focus:ring-[#397DFF] font-mono"
                          placeholder="0.00"
                        />
                        <span className="text-sm text-[#687175]">€/MWh</span>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 bg-[#F5F6F9] border-t border-[#BFC6CC]">
          <p className="text-sm text-[#687175]">
            💡 La matrice détermine le taux TICFE optimisé selon le ratio du client et son activité
          </p>
        </div>
      </div>

      {/* Exemple */}
      <div className="mt-6 p-6 bg-[#F5F6F9] border border-[#BFC6CC] rounded-lg">
        <h3 className="font-heading font-semibold text-[#191D1E] mb-3">Exemple d'utilisation</h3>
        <p className="text-sm text-[#687175] leading-relaxed">
          Un client avec un <strong>ratio de 3%</strong> (TICFE/VA) et une activité <strong>électro-intensive</strong> 
          se trouve sur la <strong>ligne 2</strong> (2,25%-6,75%) et <strong>colonne 1</strong> (électro-intensive).
          <br />
          → Taux optimisé = <strong className="text-[#397DFF]">{matrice[2].electro_intensive} €/MWh</strong>
        </p>
      </div>
      {/* Modals */}
      {showAddSeuil && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8">
            <h3 className="text-xl font-heading font-semibold text-[#191D1E] mb-6">Ajouter un Seuil</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Libellé</label>
                <input type="text" value={newSeuil.label} onChange={(e) => setNewSeuil({...newSeuil, label: e.target.value})} placeholder="Ex: 100% - 150%" className="w-full px-4 py-2 border border-[#BFC6CC] rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#191D1E] mb-2">Min (%)</label>
                  <input type="number" step="0.1" value={newSeuil.min} onChange={(e) => setNewSeuil({...newSeuil, min: e.target.value})} placeholder="100" className="w-full px-4 py-2 border border-[#BFC6CC] rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#191D1E] mb-2">Max (%)</label>
                  <input type="number" step="0.1" value={newSeuil.max} onChange={(e) => setNewSeuil({...newSeuil, max: e.target.value})} placeholder="150" className="w-full px-4 py-2 border border-[#BFC6CC] rounded-lg" />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowAddSeuil(false)} className="flex-1 px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg">Annuler</button>
                <button onClick={handleAddSeuil} className="flex-1 bg-[#397DFF] text-white py-3 rounded-lg">Ajouter</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddMotif && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8">
            <h3 className="text-xl font-heading font-semibold text-[#191D1E] mb-6">Ajouter un Motif</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Nom du Motif</label>
                <input type="text" value={newMotif.label} onChange={(e) => setNewMotif({...newMotif, label: e.target.value})} placeholder="Ex: Minéralogie" className="w-full px-4 py-2 border border-[#BFC6CC] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Couleur</label>
                <select value={newMotif.color} onChange={(e) => setNewMotif({...newMotif, color: e.target.value})} className="w-full px-4 py-2 border border-[#BFC6CC] rounded-lg">
                  <option value="bg-[#397DFF]">Bleu Azur</option>
                  <option value="bg-[#F9C80E]">Ambre</option>
                  <option value="bg-[#10D183]">Émeraude</option>
                  <option value="bg-[#687175]">Gris</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowAddMotif(false)} className="flex-1 px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg">Annuler</button>
                <button onClick={handleAddMotif} className="flex-1 bg-[#397DFF] text-white py-3 rounded-lg">Ajouter</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMatriceTauxOptimises;
